import { Component, type ErrorInfo, type ReactNode } from 'react'

interface WatchBoundaryProps {
  /** Names the part in the console when it fails. */
  name: string
  /** What shows in the part's place: quiet, a few words. A function gets `retry`, for a Try again that redraws the part. */
  fallback: ReactNode | ((retry: () => void) => ReactNode)
  /** When it changes (another event, another stage), the part gets another try. */
  resetKey?: unknown
  children: ReactNode
}

interface WatchBoundaryState {
  failed: boolean
}

/**
 * Keeps a part of the watch screen that fails to draw (the map, the chat) from blanking the whole screen: the part
 * gives way to its fallback, and the canvas plays on. The part gets another try when `resetKey` changes or the
 * fallback calls `retry` (it mounts afresh: the failed one was already unmounted).
 */
export class WatchBoundary extends Component<WatchBoundaryProps, WatchBoundaryState> {
  state: WatchBoundaryState = { failed: false }

  static getDerivedStateFromError(): WatchBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[watch] ${this.props.name} failed to render`, error, info.componentStack)
  }

  componentDidUpdate(previous: WatchBoundaryProps) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false })
  }

  retry = () => this.setState({ failed: false })

  render() {
    if (!this.state.failed) return this.props.children
    const { fallback } = this.props
    return typeof fallback === 'function' ? fallback(this.retry) : fallback
  }
}
